import { enqueueSnackbar } from "notistack";

type Headers = string[] | undefined | null;

interface DuplicateHeadersResult {
  hasDuplicates: boolean;
  duplicates: string[];
}

interface FormatBytesOptions {
  decimals?: number;
  sizeType?: "accurate" | "normal";
}

interface TrailingHeadersResult {
  cleanedHeaders: string[];
  removedCount: number;
}

export interface EmptyHeadersResult {
  hasEmptyHeaders: boolean;
  emptyIndices: number[];
  emptyCount: number;
  cleanedHeaders: string[];
}

export const hasDuplicateHeaders = (headers: Headers): DuplicateHeadersResult => {
  if (!headers || headers.length === 0) {
    return { hasDuplicates: false, duplicates: [] };
  }

  try {
    const headerMap = new Map<string, number>();
    const duplicates: string[] = [];

    headers.forEach((header, _index) => {
      if (headerMap.has(header)) {
        const count = headerMap.get(header) as number;
        if (count === 1) {
          duplicates.push(header);
        }
        headerMap.set(header, count + 1);
      } else {
        headerMap.set(header, 1);
      }
    });

    const result = { hasDuplicates: duplicates.length > 0, duplicates };

    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Something went wrong";

    enqueueSnackbar(`Error processing headers: ${errorMessage}`, {
      variant: "error",
      autoHideDuration: 5000,
    });

    return { hasDuplicates: false, duplicates: [] };
  }
};

export function formatBytes(bytes: number, opts: FormatBytesOptions = {}): string {
  const { decimals = 0, sizeType = "normal" } = opts;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === "accurate" ? accurateSizes[i] ?? "Bytes" : sizes[i] ?? "Bytes"
  }`;
}

export const detectEmptyHeaders = (headers: string[]): EmptyHeadersResult => {
  if (!headers || headers.length === 0) {
    return { hasEmptyHeaders: false, emptyIndices: [], emptyCount: 0, cleanedHeaders: [] };
  }

  const trimmedHeaders = headers.map((header) => header.trim());
  const reversedHeaders = [...trimmedHeaders].reverse();
  const firstNonEmptyIndex = reversedHeaders.findIndex((header) => header !== "");
  const emptyCount = firstNonEmptyIndex === -1 ? trimmedHeaders.length : firstNonEmptyIndex;
  const cleanedHeaders = firstNonEmptyIndex === -1 ? [] : reversedHeaders.slice(firstNonEmptyIndex).reverse();
  const emptyIndices = trimmedHeaders
    .map((header, index) => (header === "" ? index : -1))
    .filter((index) => index !== -1);

  const result = {
    hasEmptyHeaders: emptyCount > 0,
    emptyIndices,
    emptyCount,
    cleanedHeaders,
  };

  return result;
};
